import { createAsyncThunk, createEntityAdapter, createSlice } from '@reduxjs/toolkit'
import _ from 'lodash'
import { http } from 'src/services/http-common'
import { getOptionNumber, genFormId, disableRequire } from 'src/utils/form'
import { removeEditorStyle } from 'src/utils/helper'

export const loadRegisterForm = createAsyncThunk(
  'load/register-form',
  async ({ eventId, isDisableRequire, slug }, { rejectWithValue }) => {
    try {
      const res = await http.get(slug ? `/register/${slug}` : `/register-form/${eventId}`)
      if (isDisableRequire) res.data = disableRequire(res.data)
      return { ...res, slug }
    } catch (error) {
      if (!error.response) throw error
      return rejectWithValue(error.response.data)
    }
  },
)

const initCustomForm = {
  input_type: 'input-text',
  question: '',
  require: false,
  show_data_to_list: false,
  text_editor: false,
}

const adapterConfig = { selectId: (e) => e.id }
const customAdapter = createEntityAdapter(adapterConfig)
const optionAdapter = createEntityAdapter(adapterConfig)
const optionItemAdapter = createEntityAdapter(adapterConfig)
const subItemAdapter = createEntityAdapter(adapterConfig)

const initTheme = {
  name: 'outline-label',
  label: {
    r: 139,
    g: 145,
    b: 153,
    a: 1,
  },
  border: {
    r: 177,
    g: 183,
    b: 193,
    a: 1,
  },
  option: {
    r: 139,
    g: 145,
    b: 153,
    a: 1,
  },
  active: {
    r: 152,
    g: 143,
    b: 237,
    a: 1,
  },
  invalid: {
    r: 229,
    g: 83,
    b: 83,
    a: 1,
  },
  background: {
    r: 255,
    g: 255,
    b: 255,
    a: 1,
  },
  card: {
    r: 255,
    g: 255,
    b: 255,
    a: 1,
  },
  'btn-bg': {
    r: 50,
    g: 31,
    b: 219,
    a: 1,
  },
  'btn-text': {
    r: 255,
    g: 255,
    b: 255,
    a: 0.87,
  },
  'body-background': {
    r: 235,
    g: 237,
    b: 239,
    a: 1,
  },
}

const initialState = {
  fetch: {
    create: null,
    loading: null,
    error: null,
    isEnd: null,
  },
  subItemModal: {
    customId: null,
    optionId: null,
  },
  theme: _.cloneDeep(initTheme),
  email: {
    subjectResponse: '',
    subjectApprove: '',
    subjectReject: '',
    senderResponse: '',
    senderApprove: '',
    senderReject: '',
    response: '',
    approve: '',
    reject: '',
  },
  acceptance: {
    require: true,
    title:
      '<p>ข้าพเจ้าตกลงยินยอมให้เครือเนชั่น เก็บรวบรวม ใช้ เปิดเผยข้อมูลส่วนบุคคลของข้าพเจ้าที่ให้ไว้ในการลงทะเบียนนี้ เพื่อติอต่อสื่อสารทางการตลาดผ่านช่องทางใดๆ การสำรวจความคิดเห็น หรือแจ้งข้อมูลข่าวสาร เสนอบริการ สิทธิประโยชน์ โปรโมชั่น</p>',
    link: 'https://www.nationgroup.com/privacy-policy',
    accept: 'ยินดี',
    reject: 'ไม่ยินดี',
  },
  default: {
    type: 'th',
  },
  custom: customAdapter.getInitialState(),
  event: {},
  customFormCollapse: {},
  reply: {
    register: '',
    end: '',
  },
}

const registerFormSlice = createSlice({
  name: 'registerForm',
  initialState: _.cloneDeep(initialState),
  reducers: {
    clear: (state) => {
      state.fetch = { ...initialState.fetch }
      state.subItemModal = { ...initialState.subItemModal }
      state.theme = _.cloneDeep(initTheme)
      state.email = { ...initialState.email }
      state.acceptance = { ...initialState.acceptance }
      state.default = { ...initialState.default }
      state.custom = customAdapter.getInitialState()
      state.event = { ...initialState.event }
      state.customFormCollapse = { ...initialState.customFormCollapse }
      state.reply = { ...initialState.reply }
    },
    setDefault: (state, { payload }) => {
      state.default = payload
    },
    changeDefault: (state, { payload }) => {
      const { checked, value } = payload
      if (checked) state.default = { ...state.default, [value]: { require: true } }
      else state.default = _.omit(state.default, [value])
    },
    changeDefaultOption: (state, { payload }) => {
      const { checked, keyValue, type } = payload
      state.default[keyValue][type] = checked
    },
    addCustom: (state) => {
      const id = genFormId('custom-')
      customAdapter.addOne(state.custom, {
        ...initCustomForm,
        id,
        options: optionAdapter.getInitialState(),
        with_other: false,
        with_other_sub_item: subItemAdapter.getInitialState(),
        section: 'A',
      })
    },
    removeCustom: (state, { payload }) => {
      customAdapter.removeOne(state.custom, payload)
    },
    changeCustom: (state, { payload }) => {
      const { id, name, value, checked } = payload
      let withOther = {}
      let inputTypeUploadFile = {}
      let inputValidate = {}
      if (name === 'input_type') {
        const optionId = genFormId('option-')
        optionAdapter.removeAll(state.custom.entities[id].options)
        if (value === 'dynamic') {
          optionAdapter.addOne(state.custom.entities[id].options, {
            ...initCustomForm,
            id: optionId,
            options: optionItemAdapter.getInitialState(),
            with_other: false,
          })
        } else if (_.includes(['radio', 'checkbox', 'dropdown'], value)) {
          optionAdapter.addOne(state.custom.entities[id].options, {
            id: optionId,
            value: `ตัวเลือก ${1}`,
            sub_item: subItemAdapter.getInitialState(),
          })
        } else if (value === 'upload-file') {
          inputTypeUploadFile = {
            require: false,
            show_data_to_list: false,
          }
        }
        withOther = { with_other: false, with_other_sub_item: subItemAdapter.getInitialState() }
        inputValidate = { input_validate: null }
      }
      customAdapter.updateOne(state.custom, {
        id,
        changes: {
          ...withOther,
          ...inputTypeUploadFile,
          ...inputValidate,
          [name]: _.includes(['require', 'show_data_to_list', 'text_editor'], name)
            ? checked
            : value,
        },
      })
    },
    changeCustomWithOther: (state, { payload }) => {
      const { customId, with_other } = payload
      subItemAdapter.removeAll(state.custom.entities[customId].with_other_sub_item)
      customAdapter.updateOne(state.custom, {
        id: customId,
        changes: { with_other },
      })
    },
    addCustomOption: (state, { payload }) => {
      const id = genFormId('option-')
      optionAdapter.addOne(state.custom.entities[payload].options, {
        id,
        value: `ตัวเลือก ${getOptionNumber(state.custom.entities[payload].options)}`,
        sub_item: subItemAdapter.getInitialState(),
      })
    },
    importCustomOption: (state, { payload }) => {
      const { customId, data } = payload
      optionAdapter.addMany(
        state.custom.entities[customId].options,
        _.map(data, (value) => {
          const id = genFormId('option-')
          return {
            id,
            value,
            sub_item: subItemAdapter.getInitialState(),
          }
        }),
      )
    },
    changeCustomOption: (state, { payload }) => {
      const { customId, optionId, value } = payload
      optionAdapter.updateOne(state.custom.entities[customId].options, {
        id: optionId,
        changes: { value },
      })
    },
    removeCustomOption: (state, { payload }) => {
      const { customId, optionId } = payload
      optionAdapter.removeOne(state.custom.entities[customId].options, optionId)
    },
    addCustomOptionDynamic: (state, { payload }) => {
      const id = genFormId('option-')
      optionAdapter.addOne(state.custom.entities[payload].options, {
        ...initCustomForm,
        id,
        options: optionAdapter.getInitialState(),
        with_other: false,
      })
    },
    changeCustomOptionDynamic: (state, { payload }) => {
      const { customId, optionId, name, value, checked } = payload
      let withOther = {}
      if (name === 'input_type') {
        if (_.includes(['radio', 'checkbox', 'dropdown'], value)) {
          const optionItemId = genFormId('option-item-')
          optionItemAdapter.removeAll(
            state.custom.entities[customId].options.entities[optionId].options,
          )
          optionItemAdapter.addOne(
            state.custom.entities[customId].options.entities[optionId].options,
            {
              id: optionItemId,
              value: `ตัวเลือก ${1}`,
            },
          )
        }
        withOther = { with_other: false }
      }
      optionAdapter.updateOne(state.custom.entities[customId].options, {
        id: optionId,
        changes: {
          ...withOther,
          [name]: _.includes(['require', 'show_data_to_list', 'text_editor'], name)
            ? checked
            : value,
        },
      })
    },
    changeCustomOptionDynamicWithOther: (state, { payload }) => {
      const { customId, optionId, with_other } = payload
      optionAdapter.updateOne(state.custom.entities[customId].options, {
        id: optionId,
        changes: { with_other },
      })
    },
    addCustomOptionDynamicItem: (state, { payload }) => {
      const { customId, optionId } = payload
      const optionItemId = genFormId('option-item-')
      optionItemAdapter.addOne(state.custom.entities[customId].options.entities[optionId].options, {
        id: optionItemId,
        value: `ตัวเลือก ${getOptionNumber(
          state.custom.entities[customId].options.entities[optionId].options,
        )}`,
      })
    },
    changeCustomOptionDynamicItem: (state, { payload }) => {
      const { customId, optionId, optionItemId, value } = payload
      optionItemAdapter.updateOne(
        state.custom.entities[customId].options.entities[optionId].options,
        {
          id: optionItemId,
          changes: {
            value,
          },
        },
      )
    },
    removeCustomOptionDynamicItem: (state, { payload }) => {
      const { customId, optionId, optionItemId } = payload
      optionItemAdapter.removeOne(
        state.custom.entities[customId].options.entities[optionId].options,
        optionItemId,
      )
    },
    setSubItemModal: (state, { payload }) => {
      const { customId, optionId } = payload
      state.subItemModal.customId = customId
      state.subItemModal.optionId = optionId
    },
    clearSubItemModal: (state) => {
      state.subItemModal = { ...initialState.subItemModal }
    },
    addSubItem: (state) => {
      const { customId, optionId } = state.subItemModal
      const isWithOther = optionId === 'with-other'
      const subItemId = genFormId('sub-item-')
      const targetState = isWithOther
        ? state.custom.entities[customId].with_other_sub_item
        : state.custom.entities[customId].options.entities[optionId].sub_item
      subItemAdapter.addOne(targetState, {
        ...initCustomForm,
        id: subItemId,
        options: optionAdapter.getInitialState(),
        with_other: false,
      })
    },
    changeSubItem: (state, { payload }) => {
      const { id, name, value, checked } = payload
      const { customId, optionId } = state.subItemModal
      const isWithOther = optionId === 'with-other'
      const targetState = isWithOther
        ? state.custom.entities[customId].with_other_sub_item
        : state.custom.entities[customId].options.entities[optionId].sub_item
      let withOther = {}
      if (name === 'input_type') {
        const newOptionId = genFormId('option-')
        optionAdapter.removeAll(targetState.entities[id].options)
        if (_.includes(['radio', 'checkbox', 'dropdown'], value)) {
          optionAdapter.addOne(targetState.entities[id].options, {
            id: newOptionId,
            value: `ตัวเลือก ${1}`,
          })
        }
        withOther = { with_other: false }
      }
      subItemAdapter.updateOne(targetState, {
        id,
        changes: { ...withOther, [name]: name === 'require' ? checked : value },
      })
    },
    removeSubItem: (state, { payload }) => {
      const { customId, optionId } = state.subItemModal
      const isWithOther = optionId === 'with-other'
      subItemAdapter.removeOne(
        isWithOther
          ? state.custom.entities[customId].with_other_sub_item
          : state.custom.entities[customId].options.entities[optionId].sub_item,
        payload,
      )
    },
    addSubItemOption: (state, { payload }) => {
      const { customId, optionId } = state.subItemModal
      const isWithOther = optionId === 'with-other'
      const newOptionId = genFormId('option-')
      const targetState = isWithOther
        ? state.custom.entities[customId].with_other_sub_item.entities[payload].options
        : state.custom.entities[customId].options.entities[optionId].sub_item.entities[payload]
            .options
      optionAdapter.addOne(targetState, {
        id: newOptionId,
        value: `ตัวเลือก ${getOptionNumber(targetState)}`,
      })
    },
    changeSubItemOption: (state, { payload }) => {
      const { value, subItemId, id } = payload
      const { customId, optionId } = state.subItemModal
      const isWithOther = optionId === 'with-other'
      const targetState = isWithOther
        ? state.custom.entities[customId].with_other_sub_item.entities[subItemId].options
        : state.custom.entities[customId].options.entities[optionId].sub_item.entities[subItemId]
            .options
      optionAdapter.updateOne(targetState, { id, changes: { value } })
    },
    changeSubItemOptionWithOther: (state, { payload }) => {
      const { subItemId, with_other } = payload
      const { customId, optionId } = state.subItemModal
      const isWithOther = optionId === 'with-other'
      const targetState = isWithOther
        ? state.custom.entities[customId].with_other_sub_item
        : state.custom.entities[customId].options.entities[optionId].sub_item
      subItemAdapter.updateOne(targetState, { id: subItemId, changes: { with_other } })
    },
    removeSubItemOption: (state, { payload }) => {
      const { subItemId, id } = payload
      const { customId, optionId } = state.subItemModal
      const isWithOther = optionId === 'with-other'
      const targetState = isWithOther
        ? state.custom.entities[customId].with_other_sub_item.entities[subItemId].options
        : state.custom.entities[customId].options.entities[optionId].sub_item.entities[subItemId]
            .options
      optionAdapter.removeOne(targetState, id)
    },
    setEmailByType: (state, { payload }) => {
      const { emailType, value } = payload
      state.email[emailType] = value
    },
    setRegisterFormConfig: (state, { payload }) => {
      setRegisterForm(state, payload)
    },
    setAcceptance: (state, { payload }) => {
      const { name, value } = payload
      state.acceptance[name] = value
    },
    setTheme: (state, { payload }) => {
      const { name, value } = payload
      state.theme[name] = value
    },
    onSubmitCreateSuccess: (state) => {
      state.fetch.create = false
    },
    orderCustomForm: (state, { payload }) => {
      state.custom.ids = payload
    },
    resetTheme: (state) => {
      state.theme = _.cloneDeep(initTheme)
    },
    setCustomFormCollapse: (state, { payload }) => {
      if (state.customFormCollapse[payload]) {
        state.customFormCollapse = _.omit(state.customFormCollapse, payload)
      } else {
        state.customFormCollapse = { ...state.customFormCollapse, [payload]: true }
      }
    },
    setCustomFormCollapseAll: (state) => {
      if (_.keys(state.customFormCollapse).length === 0) {
        state.customFormCollapse = _.reduce(
          state.custom.ids,
          (res, n) => ({ ...res, [n]: true }),
          {},
        )
      } else {
        state.customFormCollapse = { ...initialState.customFormCollapse }
      }
    },
    setReplyByType: (state, { payload }) => {
      const { replyType, value } = payload
      state.reply[replyType] = value
    },
    setDefaultType: (state, { payload }) => {
      state.default.type = payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loadRegisterForm.pending, (state) => {
      state.fetch.create = initialState.fetch.create
      state.fetch.loading = true
      state.fetch.error = null
    })
    builder.addCase(loadRegisterForm.fulfilled, (state, { payload }) => {
      state.fetch.loading = false
      if (payload.code !== 200) {
        state.fetch.error = payload.message
        return
      }

      if (_.isEmpty(payload.data?.reply)) {
        state.reply = { ...initialState.reply }
      } else {
        state.reply = {
          ...initialState.reply,
          ...payload.data.reply,
        }
      }

      const isEnd = !!payload.data?.expired
      state.fetch.isEnd = isEnd
      if (isEnd) {
        state.fetch.create = false
        if (_.isEmpty(payload.data?.event)) {
          state.event = { ...initialState.event }
        } else {
          state.event = payload.data.event
        }
        return
      }

      state.fetch.error = null
      const isCreate = payload.data === null || payload.data?.acceptanceForm === undefined
      state.fetch.create = isCreate
      if (isCreate) {
        state.email = {
          ...state.email,
          subjectResponse: 'ข้อมูลการลงทะเบียนของท่าน กำลังอยู่ระหว่างการตรวจสอบจากเจ้าหน้าที่',
          subjectApprove: 'ขอแจ้งยืนยันการตอบรับเข้าร่วม "{event-name}"',
          subjectReject: 'ที่นั่งเต็มแล้ว ! "{event-name}"',
          senderResponse: 'ทีมงานเนชั่นทีวี',
          senderApprove: 'ทีมงานเนชั่นทีวี',
          senderReject: 'ทีมงานเนชั่นทีวี',
          response:
            '<p><span style="color:rgba(44,56,74,0.95);">เรียน {name}</span></p><p style="margin-left:40px;"><span style="color:rgba(44,56,74,0.95);">ขอขอบคุณที่ท่านได้ให้ความสนใจ “<strong>{event-name}</strong>”</span></p><p style="margin-left:40px;">ใน{date}</p><p style="margin-left:40px;"><span style="color:rgba(44,56,74,0.95);">ภายใน{reply-date}&nbsp;จะมีอีเมลแจ้งผลการลงทะเบียนให้ท่านทราบอีกครั้งหนึ่ง</span></p><p><span style="color:rgba(44,56,74,0.95);">ขอแสดงความนับถือ,</span></p><p>&nbsp;</p>',
          approve:
            '<p><span style="color:rgba(44,56,74,0.95);">เรียน {name}</span></p><p style="margin-left:40px;"><span style="color:rgba(44,56,74,0.95);">ขอแจ้งยืนยันการตอบรับเข้าร่วม “<strong>{event-name}</strong>”</span></p><figure class="table" style="width:50.12%;"><table class="ck-table-resized"><colgroup><col style="width:46.19%;"><col style="width:53.81%;"></colgroup><tbody><tr><td>วันสัมมนา</td><td>{date}</td></tr><tr><td>เวลา</td><td>{time}</td></tr><tr><td>สถานที่</td><td>{location}</td></tr></tbody></table></figure><p style="margin-left:40px;">กรุณานำคิวอาร์โค้ดนี้ พร้อมบัตรประชาชนตัวจริง แสดง ณ จุดลงทะเบียน</p><p style="text-align:center;">{qr-code-image}</p><p><strong>มาตรการเฝ้าระวังไวรัสโคโรนา หรือ โควิด-19 สำหรับเข้าร่วมงานสัมมนา</strong></p><ul><li>ผู้เข้าร่วมสัมมนาทุกท่านต้องสวมใส่หน้ากากอนามัยหรือหน้ากากผ้าตลอดเวลาที่เข้าร่วม</li><li>ผู้จัดงานเตรียมจุดวัดอุณหภูมิร่างกายก่อนเข้าพื้นที่จัดงาน และอุณหภูมิร่างกายของผู้เข้าร่วมสัมมนาต้องไม่เกิน 37.5 องศาเซลเซียล</li><li>ผู้จัดงานเตรียมจุดบริการเจลล้างมือโดยรอบบริเวณพื้นที่จัดสัมมนา</li><li>การจัดที่นั่งในพื้นที่จัดงานในรูปแบบ Physical Distancing เว้นระยะห่างบุคคลอย่างน้อย 1.0 เมตร (ระยะปลอดภัย) และรับผู้เข้าอบรมจำนวนจำกัด</li></ul><p><strong>สอบถามข้อมูลเพิ่มเติม Call center โทร. 02-338-3000 กด 1</strong></p><p>&nbsp;</p><p><span style="color:rgba(44,56,74,0.95);">ขอแสดงความนับถือ,</span></p><p>&nbsp;</p>',
          reject:
            '<p><span style="color:rgba(44,56,74,0.95);">เรียน {name}</span></p><p style="margin-left:40px;"><span style="color:rgba(44,56,74,0.95);">ขออภัยที่ต้องแจ้งให้ท่านทราบว่า “<strong>{event-name}</strong>”</span></p><p>{date}&nbsp;<span style="color:rgba(44,56,74,0.95);"> ที่นั่งถูกสำรองเต็มจำนวนแล้ว</span></p><p style="margin-left:40px;"><span style="color:rgba(44,56,74,0.95);">ท่านสามารถรับชมถ่ายทอดสดงาน “<strong>{event-name}</strong>”</span></p><p style="margin-left:40px;">ผ่านช่องทางการรับชม ดังนี้</p><p style="margin-left:40px;">&nbsp;</p><p style="margin-left:40px;">ตั้งแต่เวลา {time} น. เป็นต้นไป</p><p style="margin-left:40px;">ขอขอบพระคุณทุกท่านที่สนใจร่วมงานสัมมนาในครั้งนี้ และ ขออภัยในความไม่สะดวกมา ณ ที่นี</p><p><span style="color:rgba(44,56,74,0.95);">ขอแสดงความนับถือ,</span></p><p>&nbsp;</p>',
        }
        state.reply.register =
          '<p style="text-align:center;"><span style="color:rgba(44,56,74,0.95);">ขอขอบคุณที่ท่านได้ให้ความสนใจ “<strong>{event-name}</strong>”</span></p><p style="text-align:center;"><span style="color:rgba(44,56,74,0.95);">ภายใน{reply-date}&nbsp;จะมีอีเมลแจ้งผลการลงทะเบียนให้ท่านทราบอีกครั้งหนึ่ง</span></p>'
        state.reply.end =
          '<p style="text-align:center;">หมดเขตลงทะเบียนรายการสัมมนา <span style="color:rgba(44,56,74,0.95);">“<strong>{event-name}</strong>”</span></p>'
        if (_.isEmpty(payload.data?.event)) {
          state.event = { ...initialState.event }
        } else {
          state.event = payload.data.event
        }
        return
      }

      setRegisterForm(state, payload.data)
    })
    builder.addCase(loadRegisterForm.rejected, (state, { payload, error }) => {
      state.isLoading = false
      state.fetch.loading = false
      state.fetch.error =
        typeof payload === 'string'
          ? payload
          : payload?.message || error.message || 'ตรวจพบสิ่งผิดพลาด'
    })
  },
})

const setRegisterForm = (state, data) => {
  const { defaultForm, customForm, acceptanceForm, email, theme, event } = data
  state.default = _.isEmpty(defaultForm) ? { ...initialState.default } : defaultForm

  if (!defaultForm.type) state.default.type = 'th'

  const customResult = _.isEmpty(customForm)
    ? customAdapter.getInitialState()
    : _.reduce(
        customForm,
        (res, item) => {
          const { id, options, with_other_sub_item, ...customData } = item
          const optionResult = _.reduce(
            options,
            (res, item) => {
              const { options, sub_item, ...optionData } = item
              let optionFilter = {}
              if (customData.input_type === 'dynamic') {
                optionFilter = {
                  ...optionFilter,
                  options: _.reduce(
                    options,
                    (res, item) => ({
                      ids: [...res.ids, item.id],
                      entities: { ...res.entities, [item.id]: item },
                    }),
                    { ids: [], entities: {} },
                  ),
                }
              } else {
                optionFilter = {
                  ...optionFilter,
                  sub_item: _.reduce(
                    sub_item,
                    (res, item) => {
                      const { options, ...subItemData } = item
                      return {
                        ids: [...res.ids, item.id],
                        entities: {
                          ...res.entities,
                          [item.id]: {
                            with_other: false,
                            options: _.reduce(
                              options,
                              (res, item) => ({
                                ids: [...res.ids, item.id],
                                entities: { ...res.entities, [item.id]: item },
                              }),
                              { ids: [], entities: {} },
                            ),
                            ...subItemData,
                          },
                        },
                      }
                    },
                    {
                      ids: [],
                      entities: {},
                    },
                  ),
                }
              }
              return {
                ids: [...res.ids, optionData.id],
                entities: {
                  ...res.entities,
                  [optionData.id]: {
                    ...optionFilter,
                    ...optionData,
                  },
                },
              }
            },
            { ids: [], entities: {} },
          )
          const withOtherResult = _.reduce(
            with_other_sub_item,
            (res, item) => {
              const { options, ...withOtherData } = item
              return {
                ids: [...res.ids, item.id],
                entities: {
                  ...res.entities,
                  [item.id]: {
                    with_other: false,
                    options: _.reduce(
                      options,
                      (res, item) => ({
                        ids: [...res.ids, item.id],
                        entities: {
                          ...res.entities,
                          [item.id]: item,
                        },
                      }),
                      { ids: [], entities: {} },
                    ),
                    ...withOtherData,
                  },
                },
              }
            },
            {
              ids: [],
              entities: {},
            },
          )
          return {
            ids: [...res.ids, id],
            entities: {
              ...res.entities,
              [id]: {
                id,
                options: optionResult,
                with_other: false,
                with_other_sub_item: withOtherResult,
                ...customData,
              },
            },
          }
        },
        customAdapter.getInitialState(),
      )
  state.custom = customResult

  if (_.isEmpty(acceptanceForm)) {
    state.acceptance = { ...initialState.acceptance }
  } else {
    state.acceptance = acceptanceForm
  }

  if (_.isEmpty(email)) {
    state.email = { ...initialState.email }
  } else {
    _.forEach(_.keys(email), (emailType) => {
      state.email[emailType] = _.includes(['response', 'approve', 'reject'], emailType)
        ? removeEditorStyle(email[emailType])
        : email[emailType] || ''
    })
  }

  if (_.isEmpty(theme)) {
    state.theme = _.cloneDeep(initTheme)
  } else {
    state.theme = theme
  }

  if (_.isEmpty(event)) {
    state.event = { ...initialState.event }
  } else {
    state.event = event
  }
}

export const registerFormAction = registerFormSlice.actions

export default registerFormSlice.reducer
